import * as React from 'react'
import Image from 'next/image'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import { useTranslation, Trans } from 'next-i18next'

import { ChevronRight } from '~/src/components/Icons'

interface SigninCoverProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>
}

const LeftWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100%',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: theme.spacing(12.5, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(7, 0),
    '& .MuiTypography-root': {
      fontSize: 48
    }
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(8, 0),
    '& .MuiTypography-root': {
      fontSize: 48,
      textAlign: 'center'
    }
  },
  '@media (max-width: 380px)': {
    padding: theme.spacing(2, 0),
    justifyContent: 'flex-end',
    '& .MuiTypography-root': {
      display: 'none'
    }
  }
}))

const Item = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 12.5),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0, 8)
  }
}))

const ImageWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 360,
  '& img': {
    objectPosition: 'right'
  },
  [theme.breakpoints.down('sm')]: {
    height: 220,
    '& img': {
      objectPosition: 'center'
    }
  },
  '@media (max-width: 380px)': {
    '& img': {
      objectPosition: 'bottom'
    }
  }
}))

export default function SigninCover({ onClick }: SigninCoverProps): JSX.Element {
  const { t } = useTranslation('signin')

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      <Grid item xs={12} sm={7} md={7} sx={{ backgroundColor: 'primary.dark' }}>
        <LeftWrapper>
          <Item>
            <Image
              alt="Toptal - Toptaste"
              src="/assets/images/logo-inverse.png"
              width={1218 / 7}
              height={329 / 7}
              quality={100}
              priority={true}
            />
          </Item>
          <ImageWrapper>
            <Image
              alt="Toptal - Toptaste"
              src="/assets/images/bg-login.png"
              layout="fill"
              objectFit="contain"
              // objectPosition="right"
              quality={100}
              priority={true}
            />
          </ImageWrapper>
          <Item>
            <Typography
              color="primary.light"
              sx={{
                fontWeight: 700,
                fontSize: '104px !important',
                lineHeight: 0
              }}
            >
              “
            </Typography>
            <Typography
              color="common.white"
              sx={{
                fontWeight: 700,
                fontSize: 45,
                maxWidth: 435,
                lineHeight: 0.9,
                '& span': { color: 'primary.light' }
              }}
            >
              <Trans
                i18nKey="quote"
                t={t}
                components={[
                  <Typography
                    component="span"
                    sx={{ fontSize: 45, fontWeight: 700 }}
                    key="quote.0"
                  />
                ]}
              />
            </Typography>
            <Typography color="common.white" sx={{ fontWeight: 700, fontSize: 24, mt: 5 }}>
              {t('author')}
            </Typography>
            <Typography color="common.white" sx={{ fontWeight: 400, fontSize: 24 }}>
              {t('role')}
            </Typography>
          </Item>
        </LeftWrapper>
      </Grid>
      <Grid item xs={12} sm={5} md={5} component={Paper} elevation={0} square>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              px: 2.5,
              pt: 6.25,
              pb: 3.75
            }}
          >
            <Typography component="h1" variant="h4" textAlign="center" gutterBottom>
              {t('title')}
            </Typography>
            <Typography sx={{ maxWidth: 230 }} component="h1" variant="body1" textAlign="center">
              {t('subtitle')}
            </Typography>
            <Box sx={{ mt: 7 }}>
              <Button
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                onClick={onClick}
                endIcon={<ChevronRight />}
              >
                {t('signInCtaLabel')}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Grid>
    </Grid>
  )
}
